// A pool of workers (from a bundled worker's source text) running tasks, with transfer of their arrays.
export interface Pool<T, R> { K: number; exec: (task: T, transfer?: Transferable[]) => Promise<R>; stop: (why: string) => void }

/** K workers (default: cores - 1, 2..8); init is posted to each first (e.g. the compiled wasm). */
export function makePool<T, R>(src: string, o: { K?: number; init?: unknown; key?: string } = {}): Pool<T, R> {
  const K = o.K ?? Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1)), key = o.key ?? 'task';
  const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
  const pool = Array.from({ length: K }, () => new Worker(url)), free = [...pool], queue: ((w: Worker) => void)[] = [];
  const pending = new Map<number, { ok: (r: R) => void; fail: (e: Error) => void }>();
  let nextId = 0, stopped = false;
  const stop = (why: string) => {
    if (stopped) return;
    stopped = true;
    for (const w of pool) w.terminate();
    URL.revokeObjectURL(url);
    for (const p of pending.values()) p.fail(new Error(why));
    pending.clear();
  };
  for (const w of pool) {
    if (o.init !== undefined) w.postMessage(o.init);
    w.onmessage = (e: MessageEvent<{ id: number; error?: string } & Record<string, unknown>>) => {
      const p = pending.get(e.data.id);
      if (!p) return;
      pending.delete(e.data.id);
      const q = queue.shift();
      if (q) q(w); else free.push(w);
      if (e.data.error) p.fail(new Error(e.data.error)); else p.ok((e.data.result ?? e.data.pack ?? null) as R);
    };
    w.onerror = e => stop(`a worker failed: ${e.message}`);
  }
  const exec = (task: T, transfer: Transferable[] = []) => new Promise<R>((ok, fail) => {
    if (stopped) { fail(new Error('cancelled')); return; }
    const go = (w: Worker) => {
      const id = nextId++;
      pending.set(id, { ok, fail });
      w.postMessage({ id, [key]: task }, [...new Set(transfer)]);
    };
    const w = free.pop();
    if (w) go(w); else queue.push(go);
  });
  return { K, exec, stop };
}
